import React, { useState } from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ArrowUpRight, Mail, MapPin, Loader2 } from "lucide-react";
import { profile } from "../mock/mock";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Please share your name, email and at least a short message (10+ chars).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        toast.error("Too many submissions from your address. Please try again later.");
        return;
      }
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        const msg = Array.isArray(detail?.detail)
          ? detail.detail.map((d) => d.msg).join(", ")
          : (detail?.detail || `Request failed (${res.status})`);
        toast.error(msg);
        return;
      }
      setSubmitted(true);
      toast.success("Message received. I'll reply within 1–2 working days.");
      setForm({ name: "", email: "", company: "", service: "", budget: "", message: "", website: "" });
    } catch (err) {
      toast.error("Network error — please try again, or email me directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" aria-labelledby="contact-heading" className="relative bg-[#0F0F0F] text-[#FAFAF7] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: pitch + meta */}
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#9a9a93] mb-3">
              — (07) Contact
            </p>
            <h2 id="contact-heading" className="font-display text-4xl md:text-6xl font-medium leading-[1.02]">
              Let&apos;s talk about<br />
              <span className="italic font-light">being found.</span>
            </h2>
            <p className="mt-6 text-[#bcbcb3] text-[15px] leading-relaxed max-w-md">
              Tell me a little about your brand and what you&apos;re trying to
              solve. I read every message myself and reply within 1–2 working
              days.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="h-5 w-5 text-[#C8FF3D] mt-0.5" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9a9a93]">
                    Email
                  </div>
                  <a
                    href={`mailto:${profile.email}`}
                    className="link-underline text-base text-[#FAFAF7]"
                  >
                    {profile.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-[#C8FF3D] mt-0.5" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9a9a93]">
                    Based in
                  </div>
                  <div className="text-base">{profile.location}</div>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-[#26262a]">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9a9a93] mb-3">
                Inbox status
              </p>
              <p className="text-[#d8d8d2] text-sm">
                <span className="text-[#C8FF3D]">●</span> {profile.availability}
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                className="border border-[#26262a] bg-[#141417] p-10 md:p-14"
                data-testid="contact-success"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C8FF3D] mb-4">
                  — Message sent
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-medium leading-tight">
                  Thanks. I&apos;ll be in touch shortly.
                </h3>
                <p className="mt-4 text-[#bcbcb3] text-[15px] max-w-md">
                  In the meantime, feel free to read the latest field notes or
                  connect on LinkedIn.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  className="btn-press mt-8 rounded-full bg-[#C8FF3D] hover:bg-[#d6ff5e] text-[#0A0A0A] h-11 px-6 text-sm font-medium gap-2"
                  data-testid="contact-send-another-button"
                >
                  Send another message <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="border border-[#26262a] bg-[#141417] p-6 md:p-10 space-y-6"
                data-testid="contact-form"
              >
                {/* Honeypot — visually hidden, real users never fill this */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label>
                    Website (leave empty)
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                      data-testid="contact-honeypot"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Your name">
                    <Input
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Doe"
                      className="dark-input"
                      data-testid="contact-name-input"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="jane@brand.com"
                      className="dark-input"
                      data-testid="contact-email-input"
                    />
                  </Field>
                </div>

                <Field label="Company">
                  <Input
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Brand or website"
                    className="dark-input"
                    data-testid="contact-company-input"
                  />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Engagement">
                    <Select value={form.service} onValueChange={(v) => update("service", v)}>
                      <SelectTrigger className="dark-input h-11" data-testid="contact-service-trigger">
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai-seo">AI SEO Strategy</SelectItem>
                        <SelectItem value="geo">Generative Engine Optimization</SelectItem>
                        <SelectItem value="aeo">Answer Engine Optimization</SelectItem>
                        <SelectItem value="audit">Audit / one-off review</SelectItem>
                        <SelectItem value="other">Something else</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Budget range">
                    <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                      <SelectTrigger className="dark-input h-11" data-testid="contact-budget-trigger">
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-10">Under €10k</SelectItem>
                        <SelectItem value="10-25">€10k – €25k</SelectItem>
                        <SelectItem value="25-50">€25k – €50k</SelectItem>
                        <SelectItem value="50-plus">€50k+</SelectItem>
                        <SelectItem value="unsure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Tell me about your project">
                  <Textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="What are you trying to solve? Where are you today? What does success look like?"
                    className="dark-input resize-none"
                    data-testid="contact-message-input"
                  />
                </Field>

                <div className="flex items-center justify-between gap-4 pt-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6b6b66] hidden md:block">
                    Replies in 1–2 working days
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="btn-press rounded-full bg-[#C8FF3D] hover:bg-[#d6ff5e] text-[#0A0A0A] h-12 px-7 text-sm font-medium gap-2 disabled:opacity-70"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        Send inquiry <ArrowUpRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .dark-input {
          background: #0F0F0F !important;
          border: 1px solid #26262a !important;
          color: #FAFAF7 !important;
          border-radius: 2px !important;
        }
        .dark-input::placeholder { color: #6b6b66 !important; }
        .dark-input:focus, .dark-input:focus-visible {
          outline: none !important;
          border-color: #C8FF3D !important;
          box-shadow: 0 0 0 1px #C8FF3D !important;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9a9a93] mb-2 block">
      {label}
    </span>
    {children}
  </label>
);

export default Contact;
